/**
 * Heart tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const heartTattoosPack: ToilePack = {
    slug: 'heart-tattoos',
    category: 'tattoo',
    name: 'Heart tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'banner', label: 'Heart and banner', subject: 'a classic heart with a ribbon banner across the middle, the banner lettered "Mom"'},
        {key: 'anatomical', label: 'Anatomical heart', subject: 'an anatomical human heart with its arteries and veins, seen slightly from the front'},
        {key: 'sacred', label: 'Sacred heart', subject: 'the Sacred Heart of Jesus, a heart circled by a crown of thorns, flames rising from the top and a small cross above'},
    ],
    styles: [
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin, the dots reading cleanly'},
    ],
    directions: ['a dagger', 'red ink', 'for dad'],
    directionHint: 'try: a dagger',
    seo: {
        title: 'Heart tattoo designs: banner heart, anatomical, sacred heart',
        metaDescription: 'Nine heart tattoo designs: a classic heart with a banner, an anatomical heart and the Sacred Heart, in three styles.',
        h1: 'Heart tattoo designs: heart with banner, anatomical heart, Sacred Heart',
        tagline: 'Three hearts, each drawn in traditional flash, fine line and dotwork on white paper.',
        intro: 'Heart tattoo designs, which carry love in many forms: a classic heart with a banner for a mother or a partner, the anatomical heart with its vessels, and the Sacred Heart of Catholic devotion, a heart circled with thorns and flames. Each is drawn as traditional flash, fine line and dotwork. The banner on the first heart is lettered as a sample; type the name or word you want, and the same styles can be asked for.',
        useCases: [
            'a heart with a name on the banner',
            'an anatomical heart for a medical or family design',
            'a Sacred Heart for the chest or back',
            'a printed sheet to compare styles',
        ],
        faq: [
            {q: 'What is on the page?', a: 'Nine heart designs: a heart with a banner, an anatomical heart and the Sacred Heart, each in three styles.'},
            {q: 'Is this a design on skin?', a: 'No. Each design is a drawing on white paper, for taking to a studio or printing.'},
            {q: 'Can I change the word on the banner?', a: 'Yes. Type the word or name you want; the result is a new drawing, not a guaranteed match.'},
            {q: 'What is the Sacred Heart?', a: 'It is the image of the heart of Jesus in Catholic devotion, drawn with a crown of thorns and flames.'},
        ],
    },
}
